import React, { useEffect, useRef, useState } from "react";
import { Settings2 } from "lucide-react";

/**
 * MicRoutingPopover — small popover above the Mic toggle that groups the
 * fine-grained mic controls (Music mode, FX send, mic gain) so the master
 * bar stays single-row on tablets while still giving DJs full control.
 * Clicking the trigger toggles the popover; clicking outside closes it.
 *
 * The pill controls + slider keep the same data-testids the testing agent
 * already hit (`radio-host-mic-music-mode`, `radio-host-mic-fx-send`,
 * `radio-host-mic-gain`) so existing automation keeps working.
 */
export default function MicRoutingPopover({
  micMusicMode, setMicMusicMode,
  micFxSend, setMicFxSend,
  micGain, setMicGain,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        data-testid="radio-host-mic-routing-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-pressed={open}
        title="Mic routing — music mode, FX send, gain"
        className={`inline-flex items-center justify-center gap-1.5 text-[10px] font-black uppercase tracking-wider py-1.5 px-2 rounded border transition w-full ${
          open
            ? "bg-tv-accent/20 text-tv-accent border-tv-accent/60"
            : "bg-white/5 text-white/60 border-white/10 hover:bg-white/10"
        }`}
      >
        <Settings2 className="w-3.5 h-3.5" />
        Routing
        {(micMusicMode || micFxSend) && (
          <span className="ml-auto inline-flex items-center gap-1 text-[9px]">
            {micMusicMode && <span className="text-amber-300">●</span>}
            {micFxSend && <span className="text-violet-300">●</span>}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="radio-host-mic-routing-popover"
          className="absolute bottom-full left-0 mb-2 w-[220px] z-30 rounded-lg bg-[#0A0D14] border border-white/15 shadow-2xl p-3 space-y-3"
        >
          <div className="text-[9px] uppercase tracking-[0.25em] font-black text-white/45 border-b border-white/10 pb-1">Mic routing</div>

          <div className="flex items-center gap-1.5">
            <button
              data-testid="radio-host-mic-music-mode"
              onClick={() => setMicMusicMode((v) => !v)}
              aria-pressed={micMusicMode}
              title="Music mode disables echo-cancellation + noise-suppression so the mic jack works as a clean line-in for phones / audio interfaces."
              className={`flex-1 text-[9px] font-black uppercase tracking-wider py-1.5 rounded border transition ${
                micMusicMode
                  ? "bg-amber-500/30 text-amber-100 border-amber-400/60"
                  : "bg-white/5 text-white/60 border-white/10 hover:bg-white/10"
              }`}
            >{micMusicMode ? "Music IN" : "Talk"}</button>
            <button
              data-testid="radio-host-mic-fx-send"
              onClick={() => setMicFxSend((v) => !v)}
              aria-pressed={micFxSend}
              title="Route the mic input through the FX bus (echo / filter / flanger). Great for music-in mode; off keeps talk clean."
              className={`text-[9px] font-black uppercase tracking-wider py-1.5 px-3 rounded border transition ${
                micFxSend
                  ? "bg-violet-500/30 text-violet-100 border-violet-400/60"
                  : "bg-white/5 text-white/60 border-white/10 hover:bg-white/10"
              }`}
            >FX</button>
          </div>

          <div>
            <div className="flex items-center justify-between text-[9px] uppercase tracking-wider font-black mb-0.5">
              <span className="text-white/55">Mic gain</span>
              <span className="text-white/60 tabular-nums">{Math.round(micGain * 100)}%</span>
            </div>
            <input
              data-testid="radio-host-mic-gain"
              type="range" min="0" max="1" step="0.02"
              value={micGain}
              onChange={(e) => setMicGain(parseFloat(e.target.value))}
              onDoubleClick={() => setMicGain(0.55)}
              title="Double-click to reset"
              className="w-full h-1.5 accent-amber-400"
            />
          </div>

          <div className="text-[10px] text-white/45 leading-snug">
            <span className="text-amber-300 font-bold">Music IN</span> turns the mic jack into a clean line-in for phones / audio interfaces.
            <span className="text-violet-300 font-bold"> FX</span> sends the mic through the studio FX bus.
          </div>
        </div>
      )}
    </div>
  );
}
